import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { SFX_MAP, SFX_PRIORITY, type SfxName } from "@/lib/sfx/sfxMap";
import { useAccessTier } from "@/hooks/useAccessTier";

/**
 * Beta-only sound effects system.
 *
 *  - Tier must be `beta` or `admin` (paid / non-beta users get no sound).
 *  - Pathname must match one of SFX_ROUTES (beta path, lessons, three
 *    beta modules).
 *  - Audio context is only unlocked after the first user gesture.
 *  - Respects user mute toggle (persisted in localStorage).
 *  - Respects prefers-reduced-motion: defaults to muted unless the user has
 *    explicitly enabled SFX.
 */

const STORAGE_KEY = "heirwise.sfx.muted";
const MASTER_VOLUME = 0.2975;
const MAX_CONCURRENT = 3;
const DEBOUNCE_MS = 90;
const POOL_SIZE = 2;

/** Pathname prefixes where beta SFX may play. */
const SFX_ROUTES = [
  "/dashboard",
  "/lesson/",
  "/budget-builder",
  "/credit-management",
  "/scam-defense",
];

function pathInSfxScope(pathname: string): boolean {
  return SFX_ROUTES.some((p) => pathname === p || pathname.startsWith(p));
}

interface SfxContextValue {
  playSfx: (name: SfxName) => void;
  muted: boolean;
  setMuted: (muted: boolean) => void;
  inScope: boolean;
  unlocked: boolean;
}

const SfxContext = createContext<SfxContextValue>({
  playSfx: () => {},
  muted: true,
  setMuted: () => {},
  inScope: false,
  unlocked: false,
});

function getReducedMotion(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  try { return window.matchMedia("(prefers-reduced-motion: reduce)").matches; } catch { return false; }
}

function readStoredMute(): boolean | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === "true") return true;
    if (raw === "false") return false;
  } catch { /* ignore */ }
  return null;
}

export function SfxProvider({ children }: { children: ReactNode }) {
  const { tier } = useAccessTier();
  const location = useLocation();

  // Default to unmuted. Previously we honored prefers-reduced-motion as a
  // mute hint, but reduced-motion targets animations, not audio — and it was
  // silently killing interaction SFX for users who never explicitly muted.
  // Respect a stored user choice when present.
  const [muted, setMutedState] = useState<boolean>(() => {
    const stored = readStoredMute();
    return stored === true;
  });
  const [unlocked, setUnlocked] = useState(false);

  // SFX available to any signed-in learner on an SFX-scope route — beta,
  // paid, and admin all hear SFX. Only `none` is excluded.
  const tierAllowed = tier !== "none";
  const routeAllowed = pathInSfxScope(location.pathname);
  const inScope = tierAllowed && routeAllowed;

  const poolRef = useRef<Map<SfxName, HTMLAudioElement[]>>(new Map());
  const lastPlayedRef = useRef<Map<SfxName, number>>(new Map());
  const concurrentRef = useRef(0);
  const unlockedRef = useRef(false);
  const inScopeRef = useRef(inScope);
  const mutedRef = useRef(muted);

  // Keep the playback callback safe even when lesson handlers were created
  // while access was still resolving. This prevents stale no-op SFX callbacks
  // from surviving into answer/decision handlers.
  inScopeRef.current = inScope;
  mutedRef.current = muted;

  const setMuted = useCallback((m: boolean) => {
    setMutedState(m);
    try { localStorage.setItem(STORAGE_KEY, String(m)); } catch { /* ignore */ }
  }, []);

  // Unlock audio on first user gesture (anywhere in the app).
  useEffect(() => {
    if (unlocked) return;
    const unlock = () => {
      unlockedRef.current = true;
      setUnlocked(true);
      // Prime browser media playback during the actual user gesture. Several
      // lesson SFX fire after async grading/loading, which can otherwise fall
      // outside the browser's activation window and get silently blocked.
      if (inScopeRef.current && !mutedRef.current) {
        try {
          const primer = new Audio(SFX_MAP.click_primary);
          primer.volume = 0;
          const played = primer.play();
          if (played && typeof played.then === "function") {
            played.then(() => {
              primer.pause();
              primer.currentTime = 0;
            }).catch(() => { /* ignore */ });
          }
        } catch { /* ignore */ }
      }
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
      window.removeEventListener("touchstart", unlock);
    };
    window.addEventListener("pointerdown", unlock, { passive: true });
    window.addEventListener("keydown", unlock);
    window.addEventListener("touchstart", unlock, { passive: true });
    return () => {
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
      window.removeEventListener("touchstart", unlock);
    };
  }, [unlocked]);

  // Preload sounds (small pool) as soon as we're in scope, regardless of
  // mute state. We force `.load()` to decode the audio buffer so the first
  // play (e.g. panel-open swoosh) doesn't stutter while the file is fetched.
  useEffect(() => {
    if (!inScope) return;
    const pool = poolRef.current;
    (Object.keys(SFX_MAP) as SfxName[]).forEach((name) => {
      if (pool.has(name)) return;
      const elements: HTMLAudioElement[] = [];
      for (let i = 0; i < POOL_SIZE; i++) {
        try {
          const a = new Audio(SFX_MAP[name]);
          a.preload = "auto";
          a.volume = MASTER_VOLUME;
          try { a.load(); } catch { /* ignore */ }
          elements.push(a);
        } catch { /* fail silently */ }
      }
      pool.set(name, elements);
    });
  }, [inScope]);

  const playSfx = useCallback((name: SfxName) => {
    if (!inScopeRef.current || mutedRef.current) return;
    if (!unlockedRef.current) {
      unlockedRef.current = true;
      setUnlocked(true);
    }
    const now = typeof performance !== "undefined" ? performance.now() : Date.now();
    const last = lastPlayedRef.current.get(name) ?? 0;
    if (now - last < DEBOUNCE_MS) return;
    if (concurrentRef.current >= MAX_CONCURRENT) {
      if ((SFX_PRIORITY[name] ?? 0) < 50) return;
    }
    lastPlayedRef.current.set(name, now);

    let pool = poolRef.current.get(name);
    if (!pool || pool.length === 0) {
      try {
        const a = new Audio(SFX_MAP[name]);
        a.volume = MASTER_VOLUME;
        pool = [a];
        poolRef.current.set(name, pool);
      } catch {
        return;
      }
    }
    const free = pool.find((el) => el.paused || el.ended);
    const node = free ?? (pool[0].cloneNode(true) as HTMLAudioElement);
    try {
      node.volume = MASTER_VOLUME;
      node.currentTime = 0;
      concurrentRef.current += 1;
      const onDone = () => {
        concurrentRef.current = Math.max(0, concurrentRef.current - 1);
        node.removeEventListener("ended", onDone);
        node.removeEventListener("error", onDone);
      };
      node.addEventListener("ended", onDone);
      node.addEventListener("error", onDone);
      const p = node.play();
      if (p && typeof p.catch === "function") {
        p.catch(() => onDone());
      }
    } catch {
      concurrentRef.current = Math.max(0, concurrentRef.current - 1);
    }
  }, []);

  const value = useMemo<SfxContextValue>(
    () => ({ playSfx, muted, setMuted, inScope, unlocked }),
    [playSfx, muted, setMuted, inScope, unlocked]
  );

  return (
    <SfxContext.Provider value={value}>
      {children}
    </SfxContext.Provider>
  );
}

export function useSfxContext() {
  return useContext(SfxContext);
}
